import React, { useMemo } from 'react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { UserAuditResult } from '../../../types/userAudit';
import type { QualitativeClaimAuditResult } from '../../../lib/audit/qualitativeClaimAuditor';

function severityClass(severity: 'block' | 'warn'): string {
  return severity === 'block'
    ? 'border-danger/40 bg-danger/10 text-text-primary'
    : 'border-warning/40 bg-warning/10 text-text-primary';
}

export function QualitativeClaimAuditPanel({
  reports,
  auditResult,
}: {
  reports: SiteRagReport[];
  auditResult?: UserAuditResult | null;
}) {
  const rows = useMemo(() => {
    const out: Array<{
      report: SiteRagReport;
      finding: QualitativeClaimAuditResult['findings'][number];
    }> = [];

    if (auditResult?.qualitativeClaimByReport) {
      for (const report of reports) {
        const snapshot = auditResult.qualitativeClaimByReport[report.id];
        for (const f of snapshot?.findings ?? []) {
          out.push({ report, finding: f });
        }
      }
    }

    for (const report of reports) {
      for (const f of report.qaFindings?.qualitativeClaimAudit?.findings ?? []) {
        if (out.some((r) => r.report.id === report.id && r.finding.claimId === f.claimId)) continue;
        out.push({ report, finding: f });
      }
    }

    return out;
  }, [auditResult?.qualitativeClaimByReport, reports]);

  const meta = useMemo(() => {
    if (auditResult?.qualitativeClaimByReport) {
      return Object.values(auditResult.qualitativeClaimByReport).reduce(
        (acc, snap) => ({
          scanned: acc.scanned + snap.claimsScanned,
          judged: acc.judged + snap.claimsJudged,
          confirmed: acc.confirmed + snap.confirmedCount,
        }),
        { scanned: 0, judged: 0, confirmed: 0 },
      );
    }
    const fromReports = reports
      .map((r) => r.qaFindings?.qualitativeClaimAudit)
      .filter(Boolean);
    return fromReports.reduce(
      (acc, snap) => ({
        scanned: acc.scanned + (snap?.claimsScanned ?? 0),
        judged: acc.judged + (snap?.claimsJudged ?? 0),
        confirmed: acc.confirmed + (snap?.confirmedCount ?? 0),
      }),
      { scanned: 0, judged: 0, confirmed: 0 },
    );
  }, [auditResult?.qualitativeClaimByReport, reports]);

  if (rows.length === 0 && meta.scanned === 0) return null;

  return (
    <section className="qualitative-claim-audit-panel mt-4" data-testid="qualitative-claim-audit-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-2">
        Qualitative Claim Audit (R-8)
      </h3>
      <p className="text-sm text-text-secondary mb-3">
        Modal and comparative claims with citations are judged against SED evidence via Gemini Flash.
        Opt-in via <code>VITE_AUDIT_ENABLE_QUALITATIVE_CLAIM_AUDIT</code> — batch / on-demand only.
      </p>
      {meta.scanned > 0 && (
        <p className="text-xs text-text-secondary mb-3">
          Scanned {meta.scanned} claim(s) · judged {meta.judged} · confirmed {meta.confirmed}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-text-secondary">No unsupported or contradicted qualitative claims.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {rows.slice(0, 25).map(({ report, finding: f }) => (
            <li
              key={`${report.id}:${f.claimId}`}
              className={`rounded border px-3 py-2 ${severityClass(f.severity as 'block' | 'warn')}`}
              data-testid={`qualitative-claim-finding-${f.claimId}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-medium">{report.reportType ?? report.id}</span>
                <span className="text-xs uppercase">{f.severity}</span>
                <span className="text-xs uppercase">{f.verdict}</span>
                <span className="text-xs font-mono">{f.sectionId}:L{f.lineNumber}</span>
              </div>
              <p className="text-xs italic mb-2">&ldquo;{f.claimText}&rdquo;</p>
              <p className="text-xs mb-1">
                Refs: {f.refIndices.map((i) => `[Ref ${i}]`).join(', ') || '—'}
              </p>
              <p className="text-xs">{f.rationale}</p>
              {f.sedFieldsConsidered.length > 0 && (
                <p className="text-xs mt-1 text-text-secondary">
                  SED: {f.sedFieldsConsidered.join(', ')}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
